/**
 * WorkflowRunnerForm — reusable JSON-Schema-driven form for triggering workflows.
 *
 * Extracted from WorkflowRunnerPanel so both the Runner tab and the Scopes
 * accordion can share the same form logic (DRY).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { triggerWorkflowV2Api, type StartWorkflowResponse, type TriggerWorkflowV2Input } from '@/features/system/orchestrator/api/workflowsV2Api';
import { coerceObject } from '@/features/system/orchestrator/components/workflowBuilder/layout';

/* ── Public types ──────────────────────────────────────────────── */

export type JsonSchema = {
  type?: string | string[];
  title?: string;
  description?: string;
  default?: any;
  enum?: any[];
  properties?: Record<string, any>;
  required?: string[];
  items?: any;
};

export type FieldKind = 'string' | 'number' | 'boolean' | 'enum' | 'json';

export type FieldSpec = {
  key: string;
  label: string;
  description?: string;
  required: boolean;
  kind: FieldKind;
  enumValues?: any[];
  rawSchema: any;
};

/* ── Pure helpers ──────────────────────────────────────────────── */

export function extractDag(definition: unknown): Record<string, any> {
  const raw = coerceObject(definition);
  const dag = (raw.dag && typeof raw.dag === 'object' && !Array.isArray(raw.dag)) ? coerceObject(raw.dag) : raw;
  return dag;
}

export function inferFieldKind(schema: JsonSchema): FieldKind {
  if (Array.isArray(schema.enum)) return 'enum';

  const t = schema.type;
  const types = Array.isArray(t) ? t : typeof t === 'string' ? [t] : [];
  const typesLower = types.map((x) => String(x).toLowerCase());

  if (types.includes('boolean')) return 'boolean';
  if (types.includes('number') || types.includes('integer')) return 'number';
  if (types.includes('object') || types.includes('array')) return 'json';

  if (typesLower.includes('floatpoint') || typesLower.includes('float') || typesLower.includes('double') || typesLower.includes('decimal')) {
    return 'number';
  }

  const fmt = typeof (schema as any).format === 'string' ? String((schema as any).format).toLowerCase() : '';
  const desc = typeof schema.description === 'string' ? schema.description.toLowerCase() : '';
  if ((schema.type === 'string' || types.includes('string')) && (fmt.includes('decimal') || fmt.includes('float') || fmt.includes('double') || desc.includes('decimal'))) {
    return 'number';
  }

  return 'string';
}

export function collectSchemaFields(schema: JsonSchema): { properties: Record<string, any>; required: Set<string> } {
  const root = coerceObject(schema) as any;

  const rootProps = coerceObject(root.properties) as Record<string, any>;
  const rootReqArr = toStringArray(root.required);

  const variants: any[] = [];
  if (Array.isArray(root.oneOf)) variants.push(...root.oneOf);
  if (Array.isArray(root.anyOf)) variants.push(...root.anyOf);

  const mergedProps: Record<string, any> = { ...rootProps };
  const requiredInAll = intersectRequired(variants);
  for (const v of variants) {
    const obj = coerceObject(v) as any;
    const p = coerceObject(obj.properties) as Record<string, any>;
    for (const [k, val] of Object.entries(p)) {
      if (mergedProps[k] === undefined) mergedProps[k] = val;
    }
  }

  const required = new Set<string>();
  for (let i = 0; i < rootReqArr.length; i++) required.add(rootReqArr[i]);
  for (let i = 0; i < requiredInAll.length; i++) required.add(requiredInAll[i]);
  return { properties: mergedProps, required };
}

export function buildInputsPayload(
  fields: FieldSpec[],
  inputs: Record<string, any>,
  jsonText: Record<string, string>,
): Record<string, any> {
  const out: Record<string, any> = {};

  for (const f of fields) {
    const v = inputs[f.key];

    if (f.kind === 'number') {
      const raw = typeof v === 'string' ? v.trim() : v;
      if (raw === '' || raw === undefined || raw === null) {
        if (f.required) throw new Error(`Missing required input: ${f.key}`);
        continue;
      }
      const num = Number(raw);
      if (!Number.isFinite(num)) throw new Error(`Invalid number for ${f.key}`);
      out[f.key] = num;
      continue;
    }

    if (f.kind === 'boolean') {
      if (v === undefined) {
        if (f.required) throw new Error(`Missing required input: ${f.key}`);
        continue;
      }
      out[f.key] = Boolean(v);
      continue;
    }

    if (f.kind === 'json') {
      const text = (jsonText[f.key] ?? '').trim();
      if (!text) {
        if (v !== undefined) out[f.key] = v;
        else if (f.required) throw new Error(`Missing required input: ${f.key}`);
        continue;
      }
      try {
        out[f.key] = JSON.parse(text);
      } catch {
        throw new Error(`Invalid JSON for ${f.key}`);
      }
      continue;
    }

    // enum / string
    const s = typeof v === 'string' ? v : v == null ? '' : String(v);
    if (!s.trim()) {
      if (f.required) throw new Error(`Missing required input: ${f.key}`);
      continue;
    }
    out[f.key] = s;
  }

  return out;
}

/* ── Internal helpers ──────────────────────────────────────────── */

function intersectRequired(variants: any[]): string[] {
  const list = Array.isArray(variants) ? variants : [];
  if (list.length === 0) return [];

  let acc: Set<string> | null = null;
  for (const v of list) {
    const obj = coerceObject(v) as any;
    const req = new Set<string>(toStringArray(obj.required));
    if (acc == null) {
      acc = req;
    } else {
      const next = new Set<string>();
      const keys = Array.from(acc);
      for (let i = 0; i < keys.length; i++) {
        const k = keys[i];
        if (req.has(k)) next.add(k);
      }
      acc = next;
    }
  }
  return acc ? Array.from(acc) : [];
}

function toStringArray(value: any): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((v) => (typeof v === 'string' ? v : null))
    .filter((v): v is string => Boolean(v && v.trim()));
}

function encodeEnumValue(v: any): string {
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

export function safeStringify(value: any): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '';
  }
}

function yymmddPrefix(d: Date): string {
  const yy = String(d.getFullYear()).slice(-2);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}${mm}${dd}_`;
}

function sanitizeFilenameLike(input: string): string {
  let s = String(input ?? '').trim();
  if (!s) return '';
  s = s.split(/[/\\]/g).pop() ?? s;

  const lastDot = s.lastIndexOf('.');
  if (lastDot > 0) s = s.slice(0, lastDot);

  s = s
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^[_-]+|[_-]+$/g, '')
    .toLowerCase();

  return s;
}

export function buildDefaultProjectName(seed: string): string {
  const prefix = yymmddPrefix(new Date());
  const base = sanitizeFilenameLike(seed || 'project') || 'project';
  if (/^\d{6}_/.test(base)) return base;
  return `${prefix}${base}`;
}

function renderTypeHint(spec: FieldSpec) {
  if (spec.kind === 'enum') return 'enum';
  if (spec.kind === 'boolean') return 'boolean (Yes/No)';
  if (spec.kind === 'number') return 'number';
  if (spec.kind === 'json') return 'json';
  return 'string';
}

/* ── FieldRow ──────────────────────────────────────────────────── */

export function FieldRow({
  spec,
  value,
  jsonText,
  onChange,
  onChangeJson,
}: {
  spec: FieldSpec;
  value: any;
  jsonText: string;
  onChange: (next: any) => void;
  onChangeJson: (next: string) => void;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-start">
      <div className="sm:col-span-1">
        <div className="text-sm text-neutral-200">
          {spec.label}{spec.required ? <span className="text-rose-400"> *</span> : null}
        </div>
        {spec.description && <div className="text-xs text-neutral-500">{spec.description}</div>}
      </div>

      <div className="sm:col-span-2">
        {spec.kind === 'enum' && (
          <select
            className="w-full px-3 py-2 rounded-md pac-glass-input text-neutral-200"
            value={value === undefined || value === null || value === '' ? '' : encodeEnumValue(value)}
            onChange={(e) => {
              const raw = e.target.value;
              if (!raw) return onChange('');
              try {
                return onChange(JSON.parse(raw));
              } catch {
                return onChange(raw);
              }
            }}
          >
            <option value="">—</option>
            {(spec.enumValues ?? []).map((v) => (
              <option key={String(v)} value={encodeEnumValue(v)}>
                {String(v)}
              </option>
            ))}
          </select>
        )}

        {spec.kind === 'boolean' && (
          <select
            className="w-full px-3 py-2 rounded-md pac-glass-input text-neutral-200"
            value={value === true ? 'true' : value === false ? 'false' : ''}
            onChange={(e) => {
              const v = e.target.value;
              if (v === 'true') return onChange(true);
              if (v === 'false') return onChange(false);
              return onChange(undefined);
            }}
          >
            <option value="">—</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        )}

        {spec.kind === 'number' && (
          <input
            type="number"
            className="w-full px-3 py-2 rounded-md pac-glass-input text-neutral-200"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
          />
        )}

        {spec.kind === 'string' && (
          <input
            type="text"
            className="w-full px-3 py-2 rounded-md pac-glass-input text-neutral-200"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
          />
        )}

        {spec.kind === 'json' && (
          <textarea
            className="w-full px-3 py-2 rounded-md pac-glass-input text-neutral-200 font-mono text-xs"
            rows={3}
            value={jsonText}
            onChange={(e) => onChangeJson(e.target.value)}
            placeholder="{ } / [ ]"
          />
        )}

        <div className="mt-1 text-xs text-neutral-600">{renderTypeHint(spec)}</div>
      </div>
    </div>
  );
}

/* ── WorkflowRunnerForm (stateful) ─────────────────────────────── */

export interface WorkflowRunnerFormProps {
  /** Workflow key, e.g. "music_production.create_project". */
  workflowKey: string;
  /** Workflow version number. */
  version: number;
  /** The raw workflow definition object (contains dag.inputs_schema). */
  definition: unknown;
  /** Called after a run is successfully started. */
  onRunStarted?: (res: StartWorkflowResponse) => void;
}

export default function WorkflowRunnerForm({
  workflowKey,
  version,
  definition,
  onRunStarted,
}: WorkflowRunnerFormProps) {
  /* ── derive schema + fields ──────────────────────────────────── */
  const inputsSchema = useMemo<JsonSchema>(() => {
    const dag = extractDag(definition);
    const schema = (dag.inputs_schema ?? dag.inputs ?? {}) as unknown;
    return coerceObject(schema) as JsonSchema;
  }, [definition]);

  const fieldSpecs = useMemo<FieldSpec[]>(() => {
    const { properties, required } = collectSchemaFields(inputsSchema);
    return Object.keys(properties)
      .sort()
      .map((key) => {
        const raw = properties[key];
        const s = coerceObject(raw) as JsonSchema;
        const label = typeof s.title === 'string' && s.title.trim() ? s.title : key;
        const description = typeof s.description === 'string' ? s.description : undefined;
        const kind = inferFieldKind(s);

        return {
          key,
          label,
          description,
          required: required.has(key),
          kind,
          enumValues: Array.isArray(s.enum) ? s.enum : undefined,
          rawSchema: raw,
        };
      });
  }, [inputsSchema]);

  /* ── form state ──────────────────────────────────────────────── */
  const [inputs, setInputs] = useState<Record<string, any>>({});
  const [jsonInputsText, setJsonInputsText] = useState<Record<string, string>>({});
  const [lastRun, setLastRun] = useState<StartWorkflowResponse | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  /* Reset defaults when definition/fieldSpecs change */
  useEffect(() => {
    const dag = extractDag(definition);
    const settingsAny = coerceObject((dag as any)?.settings);
    const runIdempotencyAny = coerceObject((settingsAny as any)?.run_idempotency);
    const scopeKeyFields: string[] = Array.isArray((runIdempotencyAny as any)?.scope_key_fields)
      ? (runIdempotencyAny as any).scope_key_fields.map((x: any) => String(x ?? '').trim()).filter(Boolean)
      : [];
    const wantsDefaultProjectName = scopeKeyFields.includes('project_name');

    const defaults: Record<string, any> = {};
    const jsonDefaults: Record<string, string> = {};
    for (const f of fieldSpecs) {
      const s = coerceObject(f.rawSchema) as JsonSchema;
      if (s.default !== undefined) {
        defaults[f.key] = s.default;
        if (f.kind === 'json') {
          jsonDefaults[f.key] = safeStringify(s.default);
        }
      } else {
        if (f.kind === 'boolean') defaults[f.key] = undefined;
        if (f.kind === 'number') defaults[f.key] = '';
        if (f.kind === 'enum') defaults[f.key] = '';
        if (f.kind === 'string') defaults[f.key] = '';
        if (f.kind === 'json') {
          defaults[f.key] = undefined;
          jsonDefaults[f.key] = '';
        }
      }

      if (wantsDefaultProjectName && f.key === 'project_name' && f.kind === 'string') {
        const existing = defaults[f.key];
        const isEmpty = existing === undefined || (typeof existing === 'string' && existing.trim() === '');
        if (isEmpty) {
          defaults[f.key] = buildDefaultProjectName(workflowKey);
        }
      }
    }
    setInputs(defaults);
    setJsonInputsText(jsonDefaults);
    setLastRun(null);
    setRunError(null);
  }, [definition, fieldSpecs.length, workflowKey]);

  /* ── mutation ────────────────────────────────────────────────── */
  const startMutation = useMutation<StartWorkflowResponse, Error, TriggerWorkflowV2Input>({
    mutationFn: (payload) => triggerWorkflowV2Api(payload),
    onSuccess: (res) => {
      setLastRun(res);
      setRunError(null);
      onRunStarted?.(res);
    },
    onError: (e) => {
      setLastRun(null);
      setRunError(e.message || 'Failed to start workflow');
    },
  });

  const onRun = async () => {
    setRunError(null);
    setLastRun(null);

    try {
      const inputsPayload = buildInputsPayload(fieldSpecs, inputs, jsonInputsText);

      await startMutation.mutateAsync({
        workflow_key: workflowKey,
        version,
        inputs: inputsPayload,
      });
    } catch (e: any) {
      setRunError(typeof e?.message === 'string' ? e.message : 'Failed to build workflow payload');
    }
  };

  /* ── render ──────────────────────────────────────────────────── */
  return (
    <div>
      <div className="text-sm text-neutral-200">
        {workflowKey}{' '}
        <span className="text-neutral-500">v{version}</span>
      </div>
      <div className="text-xs text-neutral-500 mt-0.5">Fill inputs from the workflow's JSON Schema.</div>

      {fieldSpecs.length === 0 && (
        <div className="mt-3 text-sm text-neutral-500">This workflow has no declared inputs.</div>
      )}

      <div className="mt-3 space-y-3">
        {fieldSpecs.map((f) => (
          <FieldRow
            key={f.key}
            spec={f}
            value={inputs[f.key]}
            jsonText={jsonInputsText[f.key] ?? ''}
            onChange={(next: any) => {
              setInputs((old) => ({ ...old, [f.key]: next }));
            }}
            onChangeJson={(next: string) => {
              setJsonInputsText((old) => ({ ...old, [f.key]: next }));
            }}
          />
        ))}
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          disabled={startMutation.isPending}
          onClick={onRun}
          className="px-4 py-2 rounded-md bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50"
        >
          {startMutation.isPending ? 'Running…' : 'Run'}
        </button>

        {runError && <div className="text-sm text-rose-400">{runError}</div>}

        {lastRun && (
          <div className="text-sm text-emerald-300">
            Started: <span className="text-neutral-200">{lastRun.workflow_run_id}</span>
          </div>
        )}
      </div>
    </div>
  );
}
